// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 4 miest
// PredajnaView — Predajňa & Eshop (Mirko 2026-07-06)
// MASTER-DETAIL podľa ústavy 53/56: ĽAVÝ stĺpec = zoznam FA (karty), PRAVÝ = detail
// zvolenej FA (hlavička + ZISKOVOSŤ FA + položky z /api/imports/invoices/:id/items).
// FVO = predajňa (maloobchod zo skladu A, BEZ zákazky — správne), FVE = eshop.
// Úhrady z MKsoft USUHRADY + banka. Ziskovosť per položka pribudne s CENASKL zo syncu.
// Ústava 45 dodatok 2026-07-06: FVO/FVE žijú tu, Faktúry→Vystavené = len stavebné.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useBreadcrumb } from '../components/Layout.jsx'
import { useToast } from '../components/Toast.jsx'
import RowMenu from '../components/RowMenu.jsx'
import { COLORS, TYPO, SPACE, RADIUS, todayLocal, hladaj } from '../theme.js'
import { useListKeyboard } from '../hooks/useListKeyboard.js'
import { useShortcuts } from '../hooks/useShortcuts.js'
import PredajnaCP from './PredajnaCP.jsx'
import PredajnaObjednavka from './PredajnaObjednavka.jsx'

const eur = (x) => (Number(x) || 0).toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
const TODAY = () => todayLocal()

const TABS = [
  { key: 'FVO', label: 'Predajňa (FVO)' },
  { key: 'FVE', label: 'Eshop (FVE)' },
  // fvu_fo_ui_v1 (16. 10. 2026): Mirko — FVU = ubytovanie apartmán VISTA, FO = faktúra ostatné (mimo sklad,
  // napr. predaj automobilu). Oboje sa od dnes ťahá z MKsoftu (dávka fvu_fo_v1) a patria sem k ostatným
  // nestavebným tržbám. Zálohy ZP/ZZ sa sem NEDOSTÁVAJÚ — príde k nim vyúčtovacia FA (ústava 80.2b).
  { key: 'FVU', label: 'Apartmán VISTA (FVU)' },
  { key: 'FO', label: 'Ostatné (FO)' },
  { key: 'CP', label: 'Cenové ponuky' },
  { key: 'OBJ', label: 'Objednávky u dodávateľov' },   // objednávky tovaru u dodávateľov (ústava 124).
  // 29. 9. 2026: názov spresnený — volala sa rovnako ako HLAVNÁ karta Objednávky (tabuľka `orders`), pritom
  // ukazuje inú tabuľku (`predajna_obj`). Mirko hľadal objednávku OB-26-0010 tu a nenašiel ju. Ústava 57.
]

function payState(f) {
  const total = Number(f.amount_total) || 0
  const paid = Number(f.amount_paid) || 0
  if (f.status === 'uhradena' || (total > 0 && paid + 0.005 >= total)) return { key: 'paid', label: 'Uhradená', color: COLORS.add, bg: 'rgba(34,139,84,0.10)' }
  if (paid > 0.005) return { key: 'partial', label: 'Čiastočne', color: COLORS.total, bg: 'rgba(176,125,24,0.10)' }
  if (f.date_due && f.date_due < TODAY()) return { key: 'overdue', label: 'Po splatnosti', color: COLORS.remove, bg: 'rgba(192,73,47,0.10)' }
  return { key: 'unpaid', label: 'Neuhradená', color: COLORS.muted, bg: COLORS.bgSoft }
}

export default function PredajnaView() {
  useBreadcrumb([{ label: 'Financie' }, { label: 'Predajňa & Eshop' }])
  const toast = useToast()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState(() => { try { return sessionStorage.getItem('predajna_tab') || 'FVO' } catch (_) { return 'FVO' } })
  const [payFilter, setPayFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [selId, setSelId] = useState(null)
  const [listOpen, setListOpen] = useState(() => { try { return sessionStorage.getItem('predajna_list') !== '0' } catch (_) { return true } })
  useEffect(() => { try { sessionStorage.setItem('predajna_list', listOpen ? '1' : '0') } catch (_) {} }, [listOpen])
  const [detail, setDetail] = useState(null)   // { head, items }
  const [detailLoading, setDetailLoading] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const r = await fetchAuth('/api/mksoft/invoices')
      const all = r.ok ? await r.json() : []
      setRows((Array.isArray(all) ? all : []).filter(f => f.typ === 'FVO' || f.typ === 'FVE' || f.typ === 'FVU' || f.typ === 'FO'))
    } catch (e) { toast.error('Načítanie zlyhalo: ' + e.message) } finally { setLoading(false) }
  }, [toast])
  useEffect(() => { reload() }, [reload])
  useEffect(() => { try { sessionStorage.setItem('predajna_tab', tab) } catch (_) {} }, [tab])

  const data = useMemo(() => rows.filter(f => f.typ === tab), [rows, tab])
  const filtered = useMemo(() => {
    let d = data
    if (payFilter !== 'all') d = d.filter(f => payState(f).key === payFilter)
    const q = search.trim().toLowerCase()
    if (q) d = d.filter(f => String(f.cislo || '').includes(q) || hladaj(String(f.customer_name || ''), q))
    return [...d].sort((a, b) => String(b.date_issued || '').localeCompare(String(a.date_issued || '')) || String(b.cislo || '').localeCompare(String(a.cislo || '')))
  }, [data, payFilter, search])

  // auto-výber prvej FA v zozname (master-detail vždy s detailom)
  useEffect(() => {
    if (!filtered.length) { setSelId(null); setDetail(null); return }
    if (!selId || !filtered.find(f => f.id === selId)) setSelId(filtered[0].id)
  }, [filtered, selId])

  // ── KLÁVESNICA (ústava 54/56): ↑↓ výber · Enter detail · / hľadať · Esc zruš · mod+C kopíruj FA ──
  const searchRef = useRef(null)
  const detailRef = useRef(null)
  useListKeyboard({
    items: filtered,
    selectedId: selId,
    onSelect: setSelId,
    onOpen: () => { try { detailRef.current && detailRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) } catch (_) {} },
    enabled: true,
  })
  useShortcuts([
    { keys: '/', label: 'Hľadať', scope: 'Predajňa & Eshop', handler: (e) => { e && e.preventDefault && e.preventDefault(); searchRef.current && searchRef.current.focus() } },
    { keys: 'escape', label: 'Zrušiť hľadanie/filter', scope: 'Predajňa & Eshop', handler: () => { setSearch(''); setPayFilter('all'); try { document.activeElement && document.activeElement.blur() } catch (_) {} } },
    { keys: 'mod+c', label: 'Kopírovať vybranú FA', scope: 'Predajňa & Eshop', handler: () => {
      const t = window.getSelection && String(window.getSelection())
      if (t) return  // používateľ kopíruje vlastný výber textu — neprepisuj
      const f = filtered.find(x => x.id === selId)
      if (!f) return
      const txt = `${f.typ} ${f.cislo} · ${f.customer_name || ''} · ${f.date_issued} · ${eur(f.amount_total)}`
      try { navigator.clipboard.writeText(txt) } catch (_) {}
    } },
  ])

  useEffect(() => {
    if (!selId) return
    let dead = false
    setDetailLoading(true)
    fetchAuth('/api/imports/invoices/' + selId + '/items')
      .then(r => r.ok ? r.json() : null)
      // fvo_polozky_v1 (16. 10. 2026, Mirko): trasa /api/imports/invoices/:id/items vracia POLE poloziek,
      // obrazovka ale citala detail.items. Pole nema .items -> detail ostal prazdny a na VSETKYCH 88 FVO/FVE
      // sa ukazovalo 'Bez poloziek' a Ziskovost '-'. FaktDetail.jsx:208 zvlada oba tvary, preto tam polozky boli.
      .then(j => { if (!dead) setDetail(Array.isArray(j) ? { head: null, items: j } : (j || null)) })
      .catch(() => { if (!dead) setDetail(null) })
      .finally(() => { if (!dead) setDetailLoading(false) })
    return () => { dead = true }
  }, [selId])

  const sums = useMemo(() => {
    const s = { n: data.length, total: 0, bez: 0, paid: 0, open: 0, openN: 0 }
    for (const f of data) {
      const t = Number(f.amount_total) || 0, p = Math.min(Number(f.amount_paid) || 0, t)
      s.total += t; s.bez += Number(f.amount_excl_vat) || 0; s.paid += p
      if (t - p > 0.01) { s.open += t - p; s.openN++ }
    }
    return s
  }, [data])
  const counts = useMemo(() => { const c = { FVO: 0, FVE: 0, FVU: 0, FO: 0 }; rows.forEach(f => { c[f.typ] = (c[f.typ] || 0) + 1 }); return c }, [rows])

  const sel = filtered.find(f => f.id === selId) || null
  const st = sel ? payState(sel) : null
  const head = detail && detail.head
  const items = (detail && detail.items) || []
  const itemsBez = items.reduce((a, it) => a + (Number(it.amount_excl_vat) || 0), 0)
  // fvo_polozky_v1: zisk sa pocita LEN z poloziek, ktore skladovu cenu naozaj maju.
  // Nula nie je znama cena — eshop (FVE) nema CENASKL ani v jednej zo 16 faktur, takze povodna
  // podmienka (cost_total != null) by ukazala skladovu cenu 0 € a zisk 100 %. FVO ma ocenenych 64 zo 72.
  const ocenene = items.filter(it => Number(it.cost_total) > 0)
  const ocenBez = ocenene.reduce((a, it) => a + (Number(it.amount_excl_vat) || 0), 0)
  const itemsSkl = ocenene.reduce((a, it) => a + (Number(it.cost_total) || 0), 0)        // CENASKL zo syncu
  const costKnown = ocenene.length > 0
  const bezCeny = items.length - ocenene.length
  const zisk = ocenBez - itemsSkl

  const card = (k, v, color, foot) => (
    <div style={{ background: '#fff', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: '8px 12px', minWidth: 128, flex: 1 }}>
      <div style={{ fontSize: 10, color: COLORS.muted, fontWeight: TYPO.weights.bold, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{k}</div>
      <div style={{ fontSize: 17, fontWeight: TYPO.weights.bold, color: color || COLORS.text, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
      {foot ? <div style={{ fontSize: 10, color: COLORS.muted }}>{foot}</div> : null}
    </div>
  )
  const row = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', fontSize: TYPO.sizes.sm }}>
      <span style={{ color: COLORS.muted }}>{label}</span><span style={{ fontWeight: TYPO.weights.medium, textAlign: 'right' }}>{value}</span>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.sm, height: 'calc(100vh - 130px)' }}>
      {/* horná lišta: podkarty + súčty */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setSelId(null) }} style={{
            padding: '7px 16px', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold, fontFamily: 'inherit', cursor: 'pointer',
            border: `1px solid ${tab === t.key ? COLORS.brand : COLORS.line}`, borderRadius: RADIUS.md,
            background: tab === t.key ? COLORS.navBg : '#fff', color: tab === t.key ? (COLORS.brandD || COLORS.brand) : COLORS.text,
          }}>{t.label}{(t.key === 'CP' || t.key === 'OBJ') ? '' : ' (' + (counts[t.key] || 0) + ')'}</button>
        ))}
        <div style={{ display: (tab === 'CP' || tab === 'OBJ') ? 'none' : 'flex', gap: 8, marginLeft: 'auto', flex: 1, maxWidth: 640 }}>
          {card('Vystavené', eur(sums.total), COLORS.text, 'bez DPH ' + eur(sums.bez))}
          {card('Uhradené', eur(sums.paid), COLORS.add)}
          {card('Zostáva', eur(sums.open), sums.open > 0.01 ? COLORS.remove : COLORS.add, sums.openN + ' FA')}
        </div>
      </div>

      {tab === 'CP' && <PredajnaCP />}
      {tab === 'OBJ' && <PredajnaObjednavka />}
      {/* MASTER-DETAIL (ústava 53/56): vľavo zoznam, vpravo detail */}
      <div style={{ display: (tab === 'CP' || tab === 'OBJ') ? 'none' : 'grid', gridTemplateColumns: (listOpen ? '360px' : '0px') + ' 14px 1fr', gap: SPACE.sm, flex: 1, minHeight: 0 }}>
        {/* ĽAVÝ — zoznam FA */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minHeight: 0, overflow: 'hidden' }}>
          <input ref={searchRef} type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Hľadať číslo / zákazníka…  ( / )" style={{ padding: '6px 10px', fontSize: TYPO.sizes.sm, fontFamily: 'inherit', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md }} />
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[['all', 'Všetky'], ['paid', 'Uhradené'], ['partial', 'Čiastočne'], ['unpaid', 'Neuhradené'], ['overdue', 'Po splatnosti']].map(([k, l]) => (
              <button key={k} onClick={() => setPayFilter(k)} title={k} style={{ padding: '3px 10px', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${payFilter === k ? COLORS.nav : COLORS.line}`, borderRadius: 20, background: payFilter === k ? 'rgba(24,95,165,0.10)' : '#fff', color: payFilter === k ? COLORS.nav : COLORS.muted }}>{l}</button>
            ))}
          </div>
          <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 2 }}>
            {loading ? <div style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted }}>Načítavam…</div> :
              filtered.length === 0 ? <div style={{ padding: SPACE.lg, textAlign: 'center', color: COLORS.muted }}>Žiadne faktúry.</div> :
              filtered.map(f => {
                const s2 = payState(f); const on = f.id === selId
                return (
                  <div key={f.id} data-row-id={f.id} onClick={() => setSelId(f.id)} style={{ cursor: 'pointer', background: on ? COLORS.bgSoft : '#fff', border: `1px solid ${on ? COLORS.brand : COLORS.line}`, borderLeft: `4px solid ${s2.color}`, borderRadius: RADIUS.md, padding: '8px 12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.sm }}>{f.cislo}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.sm, fontVariantNumeric: 'tabular-nums' }}>{eur(f.amount_total)}</span>
                        <span onClick={e => e.stopPropagation()}><RowMenu compact actions={[
                          { key: 'copy', label: 'Kopírovať údaje FA', onClick: () => { try { navigator.clipboard.writeText(`${f.typ} ${f.cislo} · ${f.customer_name || ''} · ${f.date_issued} · ${(Number(f.amount_total) || 0).toFixed(2)} €`) } catch (_) {} } },
                          { key: 'copynum', label: 'Kopírovať číslo', onClick: () => { try { navigator.clipboard.writeText(String(f.cislo || '')) } catch (_) {} } },
                        ]} /></span>
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginTop: 2 }}>
                      <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.customer_name || '—'} · {f.date_issued}</span>
                      <span style={{ fontSize: 10, fontWeight: TYPO.weights.bold, color: s2.color, whiteSpace: 'nowrap' }}>{s2.label}</span>
                    </div>
                  </div>
                )
              })}
          </div>
        </div>

        {/* zbalenie ľavého stĺpca (ústava 56) */}
        <div onClick={() => setListOpen(o => !o)} title={listOpen ? 'Zbaliť zoznam' : 'Rozbaliť zoznam'} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.muted, fontSize: 13, userSelect: 'none', borderRadius: 4, background: COLORS.bgSoft, border: `1px solid ${COLORS.line}` }}>{listOpen ? '«' : '»'}</div>
        {/* PRAVÝ — detail FA: ziskovosť + položky */}
        <div ref={detailRef} style={{ background: '#fff', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, overflow: 'auto', minHeight: 0 }}>
          {!sel ? <div style={{ padding: SPACE.xl, textAlign: 'center', color: COLORS.muted }}>Vyber faktúru vľavo.</div> : (
            <div style={{ padding: SPACE.md, display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
              {/* hlavička */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 19, fontWeight: TYPO.weights.bold }}>{tab} {sel.cislo}</div>
                  <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>{sel.customer_name || '—'}</div>
                </div>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold, color: st.color, background: st.bg, padding: '4px 12px', borderRadius: 20 }}>{st.label}</span>
                  <RowMenu actions={[
                    { key: 'copy', label: 'Kopírovať údaje FA', onClick: () => { try { navigator.clipboard.writeText(`${sel.typ} ${sel.cislo} · ${sel.customer_name || ''} · ${sel.date_issued} · ${(Number(sel.amount_total) || 0).toFixed(2)} €`) } catch (_) {} } },
                    { key: 'copyitems', label: 'Kopírovať položky', onClick: () => { try { navigator.clipboard.writeText(items.map(it => `${it.name} · ${it.qty} ${it.unit} · ${(Number(it.amount_excl_vat) || 0).toFixed(2)} € bez DPH`).join('\n')) } catch (_) {} } },
                  ]} />
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.md }}>
                <div style={{ background: COLORS.bgSoft, borderRadius: RADIUS.md, padding: '10px 14px' }}>
                  {row('Vystavená', sel.date_issued || '—')}
                  {row('Splatnosť', sel.date_due || '—')}
                  {row('VS', sel.vs || sel.cislo)}
                  {row('Vybavuje', sel.handled_by || '—')}
                </div>
                <div style={{ background: COLORS.bgSoft, borderRadius: RADIUS.md, padding: '10px 14px' }}>
                  {row('Suma s DPH', eur(sel.amount_total))}
                  {row('Bez DPH', eur(sel.amount_excl_vat))}
                  {row('Uhradené', (Number(sel.amount_paid) || 0) > 0 ? eur(sel.amount_paid) : '—')}
                  {row('Zostáva', eur(Math.max(0, (Number(sel.amount_total) || 0) - (Number(sel.amount_paid) || 0))))}
                </div>
              </div>
              {/* ziskovosť FA */}
              <div style={{ border: `1px solid rgba(29,78,216,0.45)`, background: COLORS.bgSoft, borderRadius: RADIUS.md, padding: '10px 14px' }}>
                <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, textTransform: 'uppercase', letterSpacing: '0.05em', color: COLORS.brandD || COLORS.brand }}>Ziskovosť faktúry</div>
                <div style={{ display: 'flex', gap: 20, marginTop: 6, flexWrap: 'wrap', fontSize: TYPO.sizes.sm }}>
                  <span>Predaj bez DPH: <b style={{ fontVariantNumeric: 'tabular-nums' }}>{eur(itemsBez || sel.amount_excl_vat)}</b></span>
                  {costKnown ? (
                    <>
                      <span>Skladová cena: <b style={{ fontVariantNumeric: 'tabular-nums' }}>{eur(itemsSkl)}</b></span>
                      <span>Zisk: <b style={{ fontVariantNumeric: 'tabular-nums', color: zisk >= 0 ? (COLORS.green || '#1a7f3c') : (COLORS.red || '#b3261e') }}>{eur(zisk)}{ocenBez ? ' (' + Math.round(zisk / ocenBez * 100) + ' %)' : ''}</b></span>
                      {bezCeny ? <span style={{ color: COLORS.muted }}>{bezCeny} z {items.length} položiek nemá skladovú cenu — zisk je len z ocenených</span> : null}
                    </>
                  ) : (
                    <>
                      <span>Skladová cena: <b style={{ color: COLORS.muted }}>{(tab === 'FVO' || tab === 'FVE') ? '— doplní sa zo syncu (CENASKL)' : '— pri tomto type sa skladová cena nevedie'}</b></span>
                      <span>Zisk: <b style={{ color: COLORS.muted }}>—</b></span>
                    </>
                  )}
                </div>
              </div>
              {/* položky */}
              <div>
                <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, textTransform: 'uppercase', letterSpacing: '0.05em', color: COLORS.muted, marginBottom: 6 }}>Položky ({items.length})</div>
                {detailLoading ? <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Načítavam položky…</div> :
                  items.length === 0 ? <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Bez položiek.</div> : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: TYPO.sizes.sm }}>
                    <thead><tr style={{ background: COLORS.bgSoft }}>
                      {['PLU', 'Názov', 'Množstvo', 'MJ', 'Cena/MJ', 'Bez DPH', 'S DPH', 'Sklad', 'Marža'].map((h, i) => (
                        <th key={h} style={{ padding: '6px 10px', textAlign: i >= 2 && i !== 3 ? 'right' : 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: COLORS.muted, borderBottom: `1px solid ${COLORS.line}`, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {items.map(it => (
                        <tr key={it.id} style={{ borderBottom: `1px solid ${COLORS.lineSoft || '#f0f0f2'}` }}>
                          <td style={{ padding: '5px 10px', color: COLORS.muted, whiteSpace: 'nowrap' }}>{it.plu ? String(parseFloat(it.plu) || it.plu) : '—'}</td>
                          <td style={{ padding: '5px 10px' }}>{it.name}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{it.qty}</td>
                          <td style={{ padding: '5px 10px' }}>{it.unit}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{eur(it.price_unit)}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{eur(it.amount_excl_vat)}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: TYPO.weights.medium }}>{eur(it.amount_total)}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: COLORS.muted }}>{Number(it.cost_total) > 0 ? eur(it.cost_total) : '—'}</td>
                          <td style={{ padding: '5px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(it.cost_total) > 0 ? eur((Number(it.amount_excl_vat) || 0) - (Number(it.cost_total) || 0)) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
